import React, { useEffect, useState } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  CircularProgress, Box, TextField, MenuItem, Select, FormControl, InputLabel, Button
} from '@mui/material';
import api from '../../services/api';
import {
  PendingActions,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BSDateDisplay from '../BSDateDisplay';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const ShopPendingList = () => {
  const [rows, setRows] = useState([]);
  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState('');
  const [requiredDateFilter, setRequiredDateFilter] = useState('');
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, karigarsRes] = await Promise.all([
        api.get('/api/shop/pending-list'),
        api.get('/api/karigar')
      ]);
      setRows(pendingRes.data);
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch pending list data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredRows = rows.filter(row => {
    const karigarMatch = !karigarFilter || row.karigar === karigarFilter;
    const karatMatch = !karatFilter || row.karat === karatFilter;
    const dateMatch = !requiredDateFilter || row.requiredDate === requiredDateFilter;
    return karigarMatch && karatMatch && dateMatch;
  });

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>
        
        <Typography variant="h4" gutterBottom color="primary">
          Shop Pending List
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Items that are pending but not yet received from karigars
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel>Karigar</InputLabel>
            <Select
              value={karigarFilter}
              label="Karigar"
              onChange={e => setKarigarFilter(e.target.value)}
            >
              <MenuItem value="">All Karigars</MenuItem>
              {karigars.map(k => (
                <MenuItem key={k.abbreviation} value={k.abbreviation}>
                  {k.abbreviation} - {k.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Karat</InputLabel>
            <Select
              value={karatFilter}
              label="Karat"
              onChange={e => setKaratFilter(e.target.value)}
            >
              <MenuItem value="">All Karats</MenuItem>
              {karatOptions.map(karat => (
                <MenuItem key={karat} value={karat}>{karat}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Required Date"
            type="date"
            value={requiredDateFilter}
            onChange={e => setRequiredDateFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Box>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Bill Name</TableCell>
                  <TableCell>Bill Number</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Karigar</TableCell>
                  <TableCell>Karat</TableCell>
                  <TableCell>Item Weight</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No pending items found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.billName || 'N/A'}</TableCell>
                      <TableCell>{row.billNumber || 'N/A'}</TableCell>
                      <TableCell>{row.itemName}</TableCell>
                      <TableCell>{row.karigar}</TableCell>
                      <TableCell>{row.karat}</TableCell>
                      <TableCell>{row.itemWeight}</TableCell>
                      <TableCell>{row.quantity}</TableCell>
                      <TableCell>
                        <BSDateDisplay date={row.requiredDate} />
                      </TableCell>
                      <TableCell>
                        <BSDateDisplay date={row.issueDate} />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            backgroundColor: 'warning.light',
                            color: 'warning.contrastText',
                            display: 'inline-block',
                            fontSize: '0.75rem'
                          }}
                        >
                          Pending
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default ShopPendingList;